import React, { useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { StandardForm, StandardTable } from '../common';
import type { FormField, TableAction, TableColumn } from '../common';
import type { BombaPiston, Equipo, EquipoBomba, Operadora } from '../../types';
import { UIHelper } from '../../utils/crudHelpers';

interface BombasPistonSectionProps {
  data: {
    operadoras: Operadora[];
    equipos: Equipo[];
    bombasPiston: BombaPiston[];
    equipoBombas: EquipoBomba[];
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

function formatPumpName(pump: BombaPiston): string {
  return `${pump.nombre} - ${pump.modelo} (${pump.diametroIn}" x ${pump.carreraIn}")`;
}

export const BombasPistonSection: React.FC<BombasPistonSectionProps> = ({ data }) => {
  const [showPumpForm, setShowPumpForm] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);

  const activeBombas = useMemo(
    () => data.bombasPiston
      .filter(bomba => bomba.active)
      .sort((a, b) =>
        a.nombre.localeCompare(b.nombre) ||
        a.modelo.localeCompare(b.modelo) ||
        a.diametroIn - b.diametroIn
      ),
    [data.bombasPiston]
  );

  const activeEquipos = useMemo(
    () => data.equipos
      .filter(equipo => equipo.habilitado)
      .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [data.equipos]
  );

  const sortedBombas = useMemo(
    () => [...data.bombasPiston].sort((a, b) =>
      a.nombre.localeCompare(b.nombre) ||
      a.modelo.localeCompare(b.modelo) ||
      a.diametroIn - b.diametroIn
    ),
    [data.bombasPiston]
  );

  const sortedAssignments = useMemo(
    () => [...data.equipoBombas].sort((a, b) => {
      const equipoA = data.equipos.find(equipo => equipo.id === a.equipoId)?.nombre || '';
      const equipoB = data.equipos.find(equipo => equipo.id === b.equipoId)?.nombre || '';
      const bombaA = data.bombasPiston.find(bomba => bomba.id === a.bombaPistonId);
      const bombaB = data.bombasPiston.find(bomba => bomba.id === b.bombaPistonId);

      return equipoA.localeCompare(equipoB) ||
        a.slot - b.slot ||
        (bombaA?.diametroIn || 0) - (bombaB?.diametroIn || 0);
    }),
    [data.bombasPiston, data.equipoBombas, data.equipos]
  );

  const pumpFormFields: FormField[] = [
    {
      key: 'nombre',
      label: 'Nombre',
      type: 'text',
      required: true,
      placeholder: 'ej: HHF1600, Bomco F-1600',
      width: '260px'
    },
    {
      key: 'modelo',
      label: 'Modelo',
      type: 'text',
      required: true,
      placeholder: 'ej: F-1600, 2010',
      width: '180px'
    },
    {
      key: 'diametroIn',
      label: 'Diametro de camisa (in)',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01,
      width: '190px'
    },
    {
      key: 'carreraIn',
      label: 'Carrera (in)',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01,
      width: '140px'
    },
    {
      key: 'presionMaxPsi',
      label: 'Presion maxima (psi)',
      type: 'number',
      required: true,
      min: 0,
      step: 1,
      width: '170px'
    }
  ];

  const assignmentFormFields: FormField[] = [
    {
      key: 'bombaPistonId',
      label: 'Bomba',
      type: 'select',
      required: true,
      width: '340px',
      options: activeBombas.map(bomba => ({ value: bomba.id, label: formatPumpName(bomba) }))
    },
    {
      key: 'equipoId',
      label: 'Equipo',
      type: 'select',
      required: true,
      width: '260px',
      options: activeEquipos.map(equipo => {
        const operadora = data.operadoras.find(op => op.id === equipo.operadoraId);
        return {
          value: equipo.id,
          label: `${equipo.nombre}${operadora ? ` - ${operadora.nombre}` : ''}`
        };
      })
    },
    {
      key: 'slot',
      label: 'Bomba Nro.',
      type: 'number',
      required: true,
      min: 1,
      max: 6,
      step: 1,
      width: '120px'
    }
  ];

  const handlePumpSubmit = async (formData: Record<string, any>) => {
    await addDoc(collection(db, 'bombasPiston'), {
      nombre: formData.nombre,
      modelo: formData.modelo,
      diametroIn: Number(formData.diametroIn),
      carreraIn: Number(formData.carreraIn),
      presionMaxPsi: Number(formData.presionMaxPsi),
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    setShowPumpForm(false);
  };

  const handleAssignmentSubmit = async (formData: Record<string, any>) => {
    const slot = Number(formData.slot);
    const duplicate = data.equipoBombas.some(assignment =>
      assignment.active &&
      assignment.equipoId === formData.equipoId &&
      assignment.slot === slot &&
      assignment.bombaPistonId === formData.bombaPistonId
    );

    if (duplicate) {
      alert('Ese equipo ya tiene esa variante asignada para ese numero de bomba.');
      return;
    }

    await addDoc(collection(db, 'equipoBombas'), {
      equipoId: formData.equipoId,
      bombaPistonId: formData.bombaPistonId,
      slot,
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    setShowAssignmentForm(false);
  };

  const handlePumpEdit = async (item: BombaPiston, key: string, newValue: any) => {
    const numericKeys = ['diametroIn', 'carreraIn', 'presionMaxPsi'];
    await updateDoc(doc(db, 'bombasPiston', item.id), {
      [key]: numericKeys.includes(key) ? Number(newValue) : newValue,
      updatedAt: serverTimestamp()
    });
  };

  const handlePumpToggle = async (item: BombaPiston) => {
    await updateDoc(doc(db, 'bombasPiston', item.id), {
      active: !item.active,
      updatedAt: serverTimestamp()
    });
  };

  const handlePumpDelete = async (item: BombaPiston) => {
    const assignmentCount = data.equipoBombas.filter(assignment => assignment.bombaPistonId === item.id).length;
    if (assignmentCount > 0) {
      alert('No se puede eliminar una bomba con asignaciones. Desactiva o elimina primero sus asignaciones.');
      return;
    }

    if (window.confirm(`Eliminar la bomba "${item.nombre}"?`)) {
      await deleteDoc(doc(db, 'bombasPiston', item.id));
    }
  };

  const handleAssignmentEdit = async (item: EquipoBomba, key: string, newValue: any) => {
    const nextAssignment = {
      ...item,
      [key]: key === 'slot' ? Number(newValue) : newValue
    };
    const duplicate = data.equipoBombas.some(assignment =>
      assignment.id !== item.id &&
      assignment.active &&
      nextAssignment.active &&
      assignment.equipoId === nextAssignment.equipoId &&
      assignment.slot === nextAssignment.slot &&
      assignment.bombaPistonId === nextAssignment.bombaPistonId
    );

    if (duplicate) {
      alert('Ese equipo ya tiene esa variante asignada para ese numero de bomba.');
      return;
    }

    await updateDoc(doc(db, 'equipoBombas', item.id), {
      [key]: nextAssignment[key as keyof EquipoBomba],
      updatedAt: serverTimestamp()
    });
  };

  const handleAssignmentToggle = async (item: EquipoBomba) => {
    await updateDoc(doc(db, 'equipoBombas', item.id), {
      active: !item.active,
      updatedAt: serverTimestamp()
    });
  };

  const handleAssignmentDelete = async (item: EquipoBomba) => {
    if (window.confirm('Eliminar esta asignacion de bomba?')) {
      await deleteDoc(doc(db, 'equipoBombas', item.id));
    }
  };

  const pumpColumns: TableColumn<BombaPiston>[] = [
    {
      key: 'nombre',
      title: 'Nombre',
      width: '24%',
      editable: true,
      editType: 'text',
      onEdit: handlePumpEdit
    },
    {
      key: 'modelo',
      title: 'Modelo',
      width: '18%',
      editable: true,
      editType: 'text',
      onEdit: handlePumpEdit
    },
    {
      key: 'diametroIn',
      title: 'Diametro',
      width: '14%',
      editable: true,
      editType: 'number',
      onEdit: handlePumpEdit,
      render: value => `${value || 0} in`
    },
    {
      key: 'carreraIn',
      title: 'Carrera',
      width: '14%',
      editable: true,
      editType: 'number',
      onEdit: handlePumpEdit,
      render: value => `${value || 0} in`
    },
    {
      key: 'presionMaxPsi',
      title: 'Presion max.',
      width: '16%',
      editable: true,
      editType: 'number',
      onEdit: handlePumpEdit,
      render: value => `${value || 0} psi`
    },
    {
      key: 'status',
      title: 'Estado',
      width: '8%',
      render: (_value, item) => item.active ? '✅' : '❌'
    }
  ];

  const pumpActions: TableAction<BombaPiston>[] = [
    {
      label: 'Estado',
      variant: 'secondary',
      icon: '🔄',
      onClick: handlePumpToggle
    },
    {
      label: 'Eliminar',
      variant: 'danger',
      onClick: handlePumpDelete
    }
  ];

  const assignmentColumns: TableColumn<EquipoBomba>[] = [
    {
      key: 'equipoId',
      title: 'Equipo',
      width: '30%',
      editable: true,
      editType: 'select',
      selectOptions: activeEquipos.map(equipo => ({ value: equipo.id, label: equipo.nombre })),
      onEdit: handleAssignmentEdit,
      render: value => {
        const equipo = data.equipos.find(eq => eq.id === value);
        if (!equipo) return '(Sin equipo)';
        const operadora = data.operadoras.find(op => op.id === equipo.operadoraId);
        return `${equipo.nombre}${operadora ? ` - ${operadora.nombre}` : ''}`;
      }
    },
    {
      key: 'bombaPistonId',
      title: 'Bomba',
      width: '44%',
      editable: true,
      editType: 'select',
      selectOptions: activeBombas.map(bomba => ({ value: bomba.id, label: formatPumpName(bomba) })),
      onEdit: handleAssignmentEdit,
      render: value => {
        const bomba = data.bombasPiston.find(item => item.id === value);
        return bomba ? formatPumpName(bomba) : '(Sin bomba)';
      }
    },
    {
      key: 'slot',
      title: 'Nro.',
      width: '10%',
      editable: true,
      editType: 'number',
      onEdit: handleAssignmentEdit
    },
    {
      key: 'status',
      title: 'Estado',
      width: '10%',
      render: (_value, item) => item.active ? '✅' : '❌'
    }
  ];

  const assignmentActions: TableAction<EquipoBomba>[] = [
    {
      label: 'Estado',
      variant: 'secondary',
      icon: '🔄',
      onClick: handleAssignmentToggle
    },
    {
      label: 'Eliminar',
      variant: 'danger',
      onClick: handleAssignmentDelete
    }
  ];

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>⚙️ Bombas a pistón</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra las configuraciones base de bomba y sus asignaciones a equipos de perforación.
        </p>
      </div>

      <div style={UIHelper.getStandardCardStyle()}>
        <h3>Catálogo de bombas</h3>

        {showPumpForm ? (
          <StandardForm
            title="Nueva bomba a pistón"
            fields={pumpFormFields}
            onSubmit={handlePumpSubmit}
            onCancel={() => setShowPumpForm(false)}
            submitLabel="Agregar"
            submitIcon="➕"
          />
        ) : (
          <button
            onClick={() => setShowPumpForm(true)}
            style={{ ...UIHelper.getStandardButtonStyle('success'), marginBottom: '20px' }}
          >
            ➕ Agregar bomba
          </button>
        )}

        <StandardTable
          data={sortedBombas}
          columns={pumpColumns}
          actions={pumpActions}
          emptyMessage="No hay bombas a pistón cargadas."
        />
      </div>

      <div style={UIHelper.getStandardCardStyle()}>
        <h3>Asignaciones a equipos</h3>
        <p style={{ color: '#666', fontSize: '14px' }}>
          Asigna una o más variantes del catálogo a cada número de bomba física del equipo.
        </p>

        {showAssignmentForm ? (
          <StandardForm
            title="Nueva asignación"
            fields={assignmentFormFields}
            onSubmit={handleAssignmentSubmit}
            onCancel={() => setShowAssignmentForm(false)}
            submitLabel="Asignar"
            submitIcon="➕"
          />
        ) : (
          <button
            onClick={() => setShowAssignmentForm(true)}
            disabled={activeBombas.length === 0 || activeEquipos.length === 0}
            style={{
              ...UIHelper.getStandardButtonStyle('success'),
              marginBottom: '20px',
              opacity: activeBombas.length === 0 || activeEquipos.length === 0 ? 0.6 : 1
            }}
          >
            ➕ Asignar bomba a equipo
          </button>
        )}

        <StandardTable
          data={sortedAssignments}
          columns={assignmentColumns}
          actions={assignmentActions}
          emptyMessage="No hay bombas asignadas a equipos."
        />
      </div>

      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e3f2fd',
        borderRadius: '12px',
        border: '2px solid #2196F3'
      }}>
        <h3 style={{ color: '#1565C0', marginBottom: '15px' }}>Resumen</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Bombas cargadas</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>{data.bombasPiston.length}</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Bombas activas</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>{activeBombas.length}</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Asignaciones activas</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF9800' }}>
              {data.equipoBombas.filter(assignment => assignment.active).length}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
